/**
 * Request Failure Reporter
 *
 * Reports a failed request to observability. Called by the request coordinator
 * from `toTypedError`, which every coordinator error exit passes through once
 * per physical request, after the retry engine has given up. So one report per
 * failure, with no retry-awareness needed here, and routes the retry engine
 * skips (login, register, logout) are covered too.
 *
 * What is reported:
 * - no response (network failure, DNS, timeout): ERROR
 * - 5xx: ERROR
 * - 4xx: WARNING, only while the manager's `reportClientErrors` is on
 *
 * A cancelled request is not a failure and is never reported. Tags carry the
 * host and the path WITHOUT the query string; the host is what shows a
 * misconfigured API base URL (the first TestFlight build called a placeholder
 * host that never resolved, and nothing reported it).
 */

import { isAxiosError } from 'axios';

import { getObservability } from '../../../di/container';
import { API_ERROR_CODES, isApiError } from '../../../types/api/errors';
import { ObservabilitySeverity } from '../../../types/observability';

const SCHEME = /^[a-z][a-z0-9+.-]*:\/\//i;

/** The host of an absolute URL, else of the base URL; `unknown` if neither. */
export function requestHost(url?: string, baseURL?: string): string {
  const absolute = url && SCHEME.test(url) ? url : baseURL;
  const match = absolute
    ? /^[a-z][a-z0-9+.-]*:\/\/([^/?#]+)/i.exec(absolute)
    : null;
  return match ? match[1] : 'unknown';
}

/** The path of a request URL, without scheme, host, query string or hash. */
export function requestPath(url?: string): string {
  if (!url) return 'unknown';
  const withoutOrigin = url.replace(/^[a-z][a-z0-9+.-]*:\/\/[^/?#]+/i, '');
  const path = withoutOrigin.split(/[?#]/)[0];
  return path || '/';
}

/**
 * Report a failed request, given the error the coordinator received and the
 * typed error it is about to return. Never throws.
 */
export function reportRequestFailure(original: unknown, typed: unknown): void {
  try {
    if (!isAxiosError(original)) return;
    if (!(typed instanceof Error) || !isApiError(typed)) return;
    if (original.code === 'ERR_CANCELED') return;

    const status = typed.status;
    const noResponse =
      typed.code === API_ERROR_CODES.NETWORK_ERROR ||
      typed.code === API_ERROR_CODES.TIMEOUT_ERROR;
    const serverError = status !== undefined && status >= 500;
    const clientError = status !== undefined && status >= 400 && status < 500;
    if (!noResponse && !serverError && !clientError) return;

    const observability = getObservability();
    if (clientError && !observability.isReportingClientErrors()) return;

    const config = original.config;
    const tags: Record<string, string> = {
      host: requestHost(config?.url, config?.baseURL),
      path: requestPath(config?.url),
      method: (config?.method ?? 'get').toUpperCase(),
      code: String(typed.code),
    };
    if (status !== undefined) tags.status = String(status);
    if (noResponse && original.code) tags.transport = original.code;

    observability.captureError(
      typed,
      clientError ? ObservabilitySeverity.WARNING : ObservabilitySeverity.ERROR,
      { component: 'RequestCoordinator' },
      tags
    );
  } catch (_e) {
    // Reporting must never change what the caller receives.
  }
}
